import { isNil } from 'lodash';

/** The fields of a sync event the buffer orders by. */
export interface SequencedEvent {
    seq?: number;
    type?: string;
    recordEvent?: boolean;
}

/**
 * Releases a partition's live sync events in sequence order: an event ahead of the next expected sequence is held until
 * the ones before it arrive or a repair fetch supplies them, so an older update is never applied over a newer one.
 */
export class SeqOrderBuffer<E extends SequencedEvent> {
    private readonly held = new Map<number, E>();

    /** @param next the next sequence expected, one past the last one applied; undefined until the first event sets it. */
    constructor(private next: number | undefined) {}

    get nextSeq(): number | undefined {
        return this.next;
    }

    get heldCount(): number {
        return this.held.size;
    }

    /** Sequences still missing between the next expected one and the highest held one. */
    get missingCount(): number {
        if (isNil(this.next) || !this.held.size) return 0;
        return Math.max(...this.held.keys()) - this.next + 1 - this.held.size;
    }

    /** True for a sequenced event below the next expected sequence: already released, or passed over when a gap was released around. */
    isBehind(event: E): boolean {
        return this.isOrdered(event) && !isNil(this.next) && event.seq! < this.next;
    }

    /** Takes one live event and returns every event now ready to apply, in order; an unsequenced event passes straight through. */
    accept(event: E): E[] {
        if (!this.isOrdered(event)) return [event];
        const seq = event.seq!;
        if (isNil(this.next)) this.next = seq;
        if (seq < this.next || this.held.has(seq)) return [];
        this.held.set(seq, event);
        return this.drain();
    }

    /**
     * Adds the events a repair fetched (in any order; only sequenced changes, never a `clear`) and returns what is now ready.
     * `stillMissing` is true while any gap remains: a sequence the fetch lacked may be allocated but not stored yet. */
    merge(events: E[]): { ready: E[], stillMissing: boolean } {
        for (const event of events) {
            if (!this.isOrdered(event) || isNil(this.next)) continue;
            const seq = event.seq!;
            if (seq >= this.next && !this.held.has(seq)) this.held.set(seq, event);
        }
        const ready = this.drain();
        return { ready, stillMissing: this.held.size > 0 };
    }

    /** Gives up on the gap: returns every held event in sequence order and moves past the highest one. */
    releaseAllSkippingGap(): E[] {
        const released = [...this.held.entries()].sort(([a], [b]) => a - b).map(([, event]) => event);
        if (this.held.size) this.next = Math.max(...this.held.keys()) + 1;
        this.held.clear();
        return released;
    }

    private drain(): E[] {
        const ready: E[] = [];
        while (!isNil(this.next) && this.held.has(this.next)) {
            ready.push(this.held.get(this.next)!);
            this.held.delete(this.next);
            this.next++;
        }
        return ready;
    }

    private isOrdered(event: E) {
        return !isNil(event.seq) && event.recordEvent !== false && event.type !== 'reset' && event.type !== 'clear';
    }
}
